/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: ObjectBaseComponent for LogicFlowStep entity — shows generic CRUD form plus step-type-specific configuration sections (LOGIC_EXECUTE, CONDITION_BRANCH, SUB_FLOW, MAPPING_LOOKUP, ENTITY_SEARCH, etc.)
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {LogicFlowStepDto} from '../../../model/dto/entity/logic-flow-step.dto';
import {LogicFlowStepTypeEnum} from '../../../enum/logic-flow-step-type.enum';
import {MvsCrudService} from '../../../../../core/shared/service/crud/mvs-crud.service';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {WidgetFunctionCallBackCreate, WidgetToolbarCreateInterface} from '../../../../../core/shared/helper/widget-function-call-back-create';

@Component({
    selector: 'lg-logic-flow-step-object-component',
    templateUrl: './lg-logic-flow-step-object.component.html',
    styleUrls: ['./lg-logic-flow-step-object.component.scss'],
    standalone: false
})
export class LgLogicFlowStepObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    dto: LogicFlowStepDto;

    /** Expose enum to template */
    LogicFlowStepTypeEnum = LogicFlowStepTypeEnum;

    /** Step type label for the header badge */
    stepTypeLabel: string = '';

    /** Step type icon class for the header badge */
    stepTypeIcon: string = '';

    /** SearchStrategy suggestions for autocomplete */
    searchStrategySuggestions: any[] = [];

    /** Widget for step bindings CRUD */
    bindingWidget: WidgetData;

    /** CRUD service for SearchStrategy entity lookups */
    private searchStrategyCrudService: MvsCrudService;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected override observerService: ObserverService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.searchStrategyCrudService = this.coreService.getCrudService('si.SearchStrategy');
    }

    onObjectChanged() {
        this.updateStepTypeMeta();
        this.refreshBindingWidget();
    }

    /**
     * Derive display metadata (label, icon) from the current stepType.
     */
    private updateStepTypeMeta(): void {
        switch (this.dto.stepType) {
            case LogicFlowStepTypeEnum.LOGIC_EXECUTE:
                this.stepTypeLabel = 'Logik ausführen';
                this.stepTypeIcon = 'fa-regular fa-play';
                break;
            case LogicFlowStepTypeEnum.CONDITION_BRANCH:
                this.stepTypeLabel = 'Bedingung';
                this.stepTypeIcon = 'fa-regular fa-diamond';
                break;
            case LogicFlowStepTypeEnum.SUB_FLOW:
                this.stepTypeLabel = 'Sub-Flow';
                this.stepTypeIcon = 'fa-regular fa-layer-group';
                break;
            case LogicFlowStepTypeEnum.MAPPING_LOOKUP:
                this.stepTypeLabel = 'Mapping';
                this.stepTypeIcon = 'fa-regular fa-right-left';
                break;
            case LogicFlowStepTypeEnum.EMIT_MESSAGE:
                this.stepTypeLabel = 'Nachricht senden';
                this.stepTypeIcon = 'fa-regular fa-message';
                break;
            case LogicFlowStepTypeEnum.ASSIGN_VARIABLE:
                this.stepTypeLabel = 'Variable zuweisen';
                this.stepTypeIcon = 'fa-regular fa-equals';
                break;
            case LogicFlowStepTypeEnum.FOR_EACH:
                this.stepTypeLabel = 'Für jedes';
                this.stepTypeIcon = 'fa-regular fa-repeat';
                break;
            case LogicFlowStepTypeEnum.TRY_CATCH:
                this.stepTypeLabel = 'Fehlerbehandlung';
                this.stepTypeIcon = 'fa-regular fa-triangle-exclamation';
                break;
            case LogicFlowStepTypeEnum.ENTITY_SEARCH:
                this.stepTypeLabel = 'Entity-Suche';
                this.stepTypeIcon = 'fa-regular fa-magnifying-glass';
                break;
            default:
                this.stepTypeLabel = 'Unbekannt';
                this.stepTypeIcon = 'fa-regular fa-question';
                break;
        }
    }

    /**
     * Handle SearchStrategy selection from autocomplete.
     */
    onSearchStrategySelect(event: any): void {
        const selected = event.value || event;
        this.dto.searchStrategyDtoId = selected.id;
        this.dto.searchStrategyDtoName = selected.name;
        this.markAsDirty();
        this.saveDtoField('searchStrategyDtoId', selected.id);
    }

    /**
     * Clear the selected SearchStrategy.
     */
    onSearchStrategyClear(): void {
        this.dto.searchStrategyDtoId = null;
        this.dto.searchStrategyDtoName = null;
        this.markAsDirty();
        this.saveDtoField('searchStrategyDtoId', null);
    }

    /**
     * Handle search strategy result variable change.
     */
    onResultVariableChange(value: string): void {
        this.dto.searchStrategyResultVariable = value;
        this.markAsDirty();
        this.saveDtoField('searchStrategyResultVariable', value);
    }

    /**
     * Persist a single field update on the DTO.
     */
    private saveDtoField(fieldName: string, fieldValue: any): void {
        const updateDto: any = {id: this.dto.id};
        updateDto[fieldName] = fieldValue;
        this.crudService.update(updateDto).subscribe({
            next: (result: LogicFlowStepDto) => {
                this.messageService.showSuccessMessage('Gespeichert', 'Schritt wurde aktualisiert.');
            },
            error: () => {
                this.messageService.showErrorMessage('Fehler', 'Änderung konnte nicht gespeichert werden.');
            }
        });
    }

    /**
     * Search for SearchStrategy entities by name.
     */
    searchSearchStrategies(event: any): void {
        const query = event.query || '';
        this.searchStrategyCrudService.search(query).subscribe((result: any) => {
            this.searchStrategySuggestions = result.entries || [];
        });
    }

    /**
     * Refresh the binding widget for this step.
     */
    private refreshBindingWidget(): void {
        /** AI: Status: "in progress", Type: Widget, SubType: Runtime, Reason: Table widget showing step bindings for current LogicFlowStep (QL) */
        this.bindingWidget = WidgetFactory.createWidgetTableEntityQl(
            'lg.flow.logicFlowStepBinding.table.step',
            'Variablenbindungen',
            'lg.LogicFlowStepBinding',
            'Keine Bindungen vorhanden',
            [FilterCriteria.create('e.flowStepId', FilterCriteria.cOperatorEqual, this.dto.id)],
            [new Sorting('e.variableName', true)]
        );

        const fields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'flowStepDtoId', fieldValue: this.dto.id}
        ];
        this.bindingWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
